import React, { Component } from 'react';
import { array, arrayOf, bool, func, number, string } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { last, path, pathOr } from 'ramda';
import { isMobile } from 'react-device-detect';

import {
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  txIsAcceptedNonRefundable,
  txIsAcceptedRefundable,
  txIsCanceled,
  txIsDeclined,
  txIsDeclinedByCustomer,
  txIsEnquired,
  txIsPaymentExpired,
  txIsPaymentPending,
  txIsRefunded,
  txIsRequested,
  txHasBeenDelivered,
  txIsPreAccepted,
  txLastTransition,
  TRANSITION_REQUEST_MANUAL_PAYMENT,
  txIsDelivered,
} from '../../util/transactions';
import { LINE_ITEM_NIGHT, LINE_ITEM_DAY, propTypes } from '../../util/types';
import {
  ensureListing,
  ensureTransaction,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { isMobileSafari } from '../../util/userAgent';
import { formatMoney } from '../../util/currency';
import {
  AvatarLarge,
  BookingPanel,
  BookingParticipantBreakdown,
  BookingTimeBreakdown,
  NamedLink,
  CancelBookingModal,
  ReviewModal,
  UserDisplayName,
  TextContainer, SecondaryButton,
} from '../../components';
import config from '../../config';

// These are internal components that make this file more readable.
import AddressLinkMaybe from './AddressLinkMaybe';
import PriceBreakdownMaybe from './PriceBreakdownMaybe';
import DetailCardHeadingsMaybe from './DetailCardHeadingsMaybe';
import DetailCardImage from './DetailCardImage';
import FeedSection from './FeedSection';
import SaleActionButtonsMaybe from './SaleActionButtonsMaybe';
import PanelHeading, {
  HEADING_ENQUIRED,
  HEADING_PAYMENT_PENDING,
  HEADING_PAYMENT_EXPIRED,
  HEADING_REQUESTED,
  HEADING_ACCEPTED,
  HEADING_DECLINED,
  HEADING_CANCELED,
  HEADING_DELIVERED, HEADING_PRE_ACCEPTED,
} from './PanelHeading';

import css from './TransactionPanel.module.css';
import SectionSimilarListings from './SectionSimilarListings';
import { stringify } from '../../util/urlHelpers';
import PaymentFormWithPaymentIntent
  from '../PaymentFormWithPaymentIntent/PaymentFormWithPaymentIntent';
import { storeData } from '../../util/checkoutSessionHelpers';
import { confirmManualPayment } from '../../containers/TransactionPage/TransactionPage.duck';
import { StripePaymentForm } from '../../forms';
import ConfirmedBadge from '../ConfirmedBadge/ConfirmedBadge';
import { getBookingDate, moment } from '../../util/dates';
import { refundBookingAfterDelivered } from '../../ducks/transaction.duck';
import {
  BookingRequestContext,
  useBookingRequest,
} from '../../containers/ListingPage/hooks/useBookingRequest';
import DeclineBookingModal from '../DeclineBookingModal';

const BookingPanelWithContext = (props) => {
  const bookingRequestProps = useBookingRequest();
  return (
    <BookingRequestContext.Provider value={bookingRequestProps}>
      <BookingPanel {...props} />
    </BookingRequestContext.Provider>
  );
};

// Helper function to get display names for different roles
const displayNames = (currentUser, currentProvider, currentCustomer, intl) => {
  const authorDisplayName = <UserDisplayName user={currentProvider} intl={intl} />;
  const customerDisplayName = <UserDisplayName user={currentCustomer} intl={intl} />;

  let otherUserDisplayName = '';
  let otherUserDisplayNameString = '';
  const currentUserIsCustomer =
    currentUser.id && currentCustomer.id && currentUser.id.uuid === currentCustomer.id.uuid;
  const currentUserIsProvider =
    currentUser.id && currentProvider.id && currentUser.id.uuid === currentProvider.id.uuid;

  if (currentUserIsCustomer) {
    otherUserDisplayName = authorDisplayName;
    otherUserDisplayNameString = userDisplayNameAsString(currentProvider, '');
  } else if (currentUserIsProvider) {
    otherUserDisplayName = customerDisplayName;
    otherUserDisplayNameString = userDisplayNameAsString(currentCustomer, '');
  }

  return {
    authorDisplayName,
    customerDisplayName,
    otherUserDisplayName,
    otherUserDisplayNameString,
  };
};

export class TransactionPanelComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isReviewModalOpen: false,
      isCancelBookingModalOpen: false,
      isRefundBookingAfterDeliveredModalOpen: false,
      reviewSubmitted: false,
    };
    this.isMobSaf = false;

    this.onOpenReviewModal = this.onOpenReviewModal.bind(this);
    this.onSubmitReview = this.onSubmitReview.bind(this);
  }

  componentDidMount() {
    this.isMobSaf = isMobileSafari();
  }

  onOpenReviewModal() {
    this.setState({ isReviewModalOpen: true });
  }

  onSubmitReview(values) {
    const { onSendReview, transaction, transactionRole } = this.props;
    const currentTransaction = ensureTransaction(transaction);
    const { reviewRating, reviewContent } = values;
    const rating = Number.parseInt(reviewRating, 10);
    onSendReview(transactionRole, currentTransaction, rating, reviewContent)
      .then(r => this.setState({ isReviewModalOpen: (rating >= 5), reviewSubmitted: true }))
      .catch(e => {
        // Do nothing.
      });
  }

  is48HoursBeforeBooking = () => {
    const startDate = path(['transaction', 'booking', 'attributes', 'start'], this.props);
    if (!startDate) return false;

    const dateToCancelMax = new Date(startDate);
    dateToCancelMax.setDate(dateToCancelMax.getDate() - 2);
    return new Date() < dateToCancelMax;
  };

  openDeclineBookingModal = () => {
    this.setState({ isDeclineBookingModalOpen: true });
  };

  handleOnDeclineBooking = (reasonData) => {
    const {
      transaction = {},
      onDeclineSale,
    } = this.props;
    const { id } = transaction;
    this.setState({ isDeclineBookingModalOpen: false });
    onDeclineSale(id, reasonData);
  };

  openCancelBookingModal = () => {
    this.setState({ isCancelBookingModalOpen: true });
  };

  handleOnCancelBooking = (reasonData) => {
    const {
      transaction = {},
      onCancelBooking,
      transactionRole,
    } = this.props;
    const { id } = transaction;

    this.setState({ isCancelBookingModalOpen: false });
    onCancelBooking(id, transactionRole, txIsAcceptedRefundable(transaction), reasonData);
  };

  openRefundBookingAfterDeliveredModal = () => {
    this.setState({ isRefundBookingAfterDeliveredModalOpen: true });
  };

  handleOnRefundBookingAfterDelivered = () => {
    const {
      transaction = {},
      onRefundBookingAfterDelivered,
    } = this.props;
    const { id } = transaction;

    this.setState({ isRefundBookingAfterDeliveredModalOpen: false });
    onRefundBookingAfterDelivered(id);
  };

  render() {
    const {
      rootClassName,
      className,
      currentUser,
      transaction,
      totalMessagePages,
      oldestMessagePageFetched,
      messages,
      initialMessageFailed,
      savePaymentMethodFailed,
      fetchMessagesInProgress,
      fetchMessagesError,
      sendReviewInProgress,
      sendReviewError,
      onManageDisableScrolling,
      onShowMoreMessages,
      transactionRole,
      intl,
      onAcceptSale,
      onDeclineSale,
      acceptInProgress,
      declineInProgress,
      acceptSaleError,
      declineSaleError,
      nextTransitions,
      cancelInProgress,
      cancelBookingError,
      fetchSimilarListingsInProgress,
      fetchSimilarListingsError,
      similarListings,
      searchLocationData = {},
      isConfirmManualPaymentMultiBookingLoading,
      confirmManualPaymentMultiBookingError,
    } = this.props;
    const currentTransaction = ensureTransaction(transaction);
    const currentListing = ensureListing(currentTransaction.listing);
    const currentProvider = ensureUser(currentTransaction.provider);
    const currentCustomer = ensureUser(currentTransaction.customer);
    const isSwimPlusCustomer = pathOr(0, ['attributes', 'profile', 'metadata', 'swimPlusLevel'], currentCustomer) > 0;
    const isCustomer = transactionRole === 'customer';
    const isProvider = transactionRole === 'provider';

    const listingCity = path(['listing', 'attributes', 'publicData', 'location', 'city'], currentTransaction);
    const suggestionSearchParams = stringify({
      address: listingCity || 'suggestion',
      ...searchLocationData,
    });
    const listingLoaded = !!currentListing.id;
    const listingDeleted = listingLoaded && currentListing.attributes.deleted;
    const iscustomerLoaded = !!currentCustomer.id;
    const isCustomerBanned = iscustomerLoaded && currentCustomer.attributes.banned;
    const isCustomerDeleted = iscustomerLoaded && currentCustomer.attributes.deleted;
    const isProviderLoaded = !!currentProvider.id;
    const isProviderBanned = isProviderLoaded && currentProvider.attributes.banned;
    const isProviderDeleted = isProviderLoaded && currentProvider.attributes.deleted;
    const stateDataFn = tx => {
      if (txIsEnquired(tx)) {
        const transitions = Array.isArray(nextTransitions)
          ? nextTransitions.map(transition => {
            return transition.attributes.name;
          })
          : [];
        const hasCorrectNextTransition =
          transitions.length > 0 && transitions.includes(TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY);
        return {
          headingState: HEADING_ENQUIRED,
          showBookingPanel: isCustomer && !isProviderBanned && hasCorrectNextTransition,
        };
      } else if (txIsPaymentPending(tx)) {
        if (txLastTransition(tx) === TRANSITION_REQUEST_MANUAL_PAYMENT) return {
          headingState: HEADING_PRE_ACCEPTED,
          showDetailCardHeadings: isCustomer,
          showSaleButtons: isProvider && !isCustomerBanned,
          showSearchButton: isCustomer,
        };

        return {
          headingState: HEADING_PAYMENT_PENDING,
          showDetailCardHeadings: isCustomer,
        };
      } else if (txIsPaymentExpired(tx)) {
        return {
          headingState: HEADING_PAYMENT_EXPIRED,
          showDetailCardHeadings: isCustomer,
        };
      } else if (txIsRequested(tx)) {
        return {
          headingState: HEADING_REQUESTED,
          showDetailCardHeadings: isCustomer,
          showSaleButtons: isProvider && !isCustomerBanned,
          showCancelButton: isCustomer,
          showSearchButton: isCustomer,
        };
      } else if (txIsAcceptedRefundable(tx)) {
        return {
          headingState: HEADING_ACCEPTED,
          showDetailCardHeadings: isCustomer,
          showAddress: isCustomer,
          showCancelButton: isCustomer || isProvider,
        };
      } else if (txIsPreAccepted(tx)) {
        return {
          headingState: HEADING_PRE_ACCEPTED,
          showDetailCardHeadings: isCustomer,
          showSearchButton: isCustomer,
        };
      } else if (txIsAcceptedNonRefundable(tx)) {
        return {
          headingState: HEADING_ACCEPTED,
          showDetailCardHeadings: isCustomer,
          showAddress: isCustomer,
          showNoCancellation: isCustomer,
        };
      } else if (txIsDeclinedByCustomer(tx)) {
        return {
          headingState: HEADING_CANCELED,
          showDetailCardHeadings: isCustomer,
          showSearchButton: isCustomer,
        };
      } else if (txIsDeclined(tx)) {
        return {
          headingState: HEADING_DECLINED,
          showDetailCardHeadings: isCustomer,
          showSearchButton: isCustomer,
        };
      } else if (txIsCanceled(tx) || txIsRefunded(tx)) {
        return {
          headingState: HEADING_CANCELED,
          showDetailCardHeadings: isCustomer,
          showSearchButton: isCustomer,
        };
      } else if (txIsDelivered(tx)) {
        return {
          headingState: HEADING_DELIVERED,
          showDetailCardHeadings: isCustomer,
          showAddress: isCustomer,
          showRefundButtonByProviderAfterDelivered: isProvider,
        };
      } else if (txHasBeenDelivered(tx)) {
        return {
          headingState: HEADING_DELIVERED,
          showDetailCardHeadings: isCustomer,
          showAddress: isCustomer,
        };
      } else {
        return { headingState: 'unknown' };
      }
    };
    const stateData = stateDataFn(currentTransaction);

    const deletedListingTitle = intl.formatMessage({
      id: 'TransactionPanel.deletedListingTitle',
    });

    const {
      authorDisplayName,
      customerDisplayName,
      otherUserDisplayName,
    } = displayNames(currentUser, currentProvider, currentCustomer, intl);

    const { publicData, geolocation } = currentListing.attributes;
    const location = publicData && publicData.location ? publicData.location : {};
    const isHighDeclineRate = pathOr(
      false,
      ['attributes', 'metadata', 'isHighDeclineRate'],
      currentListing
    );
    const listingTitle = currentListing.attributes.deleted
      ? deletedListingTitle
      : currentListing.attributes.title;

    const unitType = config.bookingUnitType;
    const isNightly = unitType === LINE_ITEM_NIGHT;
    const isDaily = unitType === LINE_ITEM_DAY;

    const unitTranslationKey = isNightly
      ? 'TransactionPanel.perNight'
      : isDaily
        ? 'TransactionPanel.perDay'
        : 'TransactionPanel.perUnit';

    const price = currentListing.attributes.price;
    const bookingSubTitle = price
      ? `${formatMoney(intl, price)} ${intl.formatMessage({ id: unitTranslationKey })}`
      : '';

    const firstImage =
      currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
    const currentUserIsCustomer =
      currentUser.id && currentCustomer.id && currentUser.id.uuid === currentCustomer.id.uuid;
    const showSimilarPools = currentUserIsCustomer && (txIsDeclined(currentTransaction) || txIsRefunded(currentTransaction)) && similarListings;
    const showButtons = stateData.showSaleButtons
      || stateData.showCancelButton
      || stateData.showRefundButtonByProviderAfterDelivered
      || stateData.showSearchButton;
    const actionButtons = (
      <SaleActionButtonsMaybe
        acceptInProgress={acceptInProgress}
        cancelInProgress={cancelInProgress}
        declineInProgress={declineInProgress}
        acceptSaleError={acceptSaleError}
        cancelBookingError={cancelBookingError}
        declineSaleError={declineSaleError}
        onAcceptSale={() => onAcceptSale(currentTransaction.id, currentListing.id)}
        onDeclineSale={this.openDeclineBookingModal}
        onCancelBooking={this.openCancelBookingModal}
        onRefundBookingAfterDelivered={this.openRefundBookingAfterDeliveredModal}
        stateData={stateData}
        transaction={currentTransaction}
      />
    );

    const showSendMessageForm =
      !isCustomerBanned && !isCustomerDeleted && !isProviderBanned && !isProviderDeleted;

    const sendingMessageNotAllowed = intl.formatMessage({
      id: 'TransactionPanel.sendingMessageNotAllowed',
    });

    const paymentMethodsPageLink = (
      <NamedLink name="PaymentMethodsPage">
        <FormattedMessage id="TransactionPanel.paymentMethodsPageLink" />
      </NamedLink>
    );
    const bookingHeadingText = intl.formatMessage({ id: 'TransactionPanel.bookingInfoHeading' });
    const noCancellationText = intl.formatMessage({ id: 'TransactionPanel.noCancellation' });
    const classes = classNames(rootClassName || css.root, className);
    const handleConfirmPayment = async (params) => {
      const {
        stripe,
        card,
        paymentParams,
      } = params;
      console.log('handleConfirmPayment', transaction, {
        transactionId: transaction.id,
        stripe,
        paymentParams,
      }, params);
      this.props.onConfirmManualPayment({
        order: transaction,
        transactionId: transaction.id,
        stripe,
        paymentParams,
      });
    };
    const handleShareBooking = async () => {
      const startDate = path(['transaction', 'booking', 'attributes', 'start'], this.props);
      const endDate = path(['transaction', 'booking', 'attributes', 'end'], this.props);
      const address = path(['transaction', 'attributes', 'protectedData', 'address'], this.props);
      const listingId = path(['transaction', 'listing', 'id', 'uuid'], this.props);
      const shareData = {
        text: intl.formatMessage({
          id: 'TransactionPanel.shareMessage',
        }, {
          date: getBookingDate(moment(startDate), moment(endDate)),
          address,
          link: `${process.env.REACT_APP_CANONICAL_ROOT_URL}/l/${listingId}`,
        }),
      };
      console.log('shareData', shareData, this.props);
      console.log('shareData navigator.canShare', window.navigator, window.navigator.canShare);
      try {
        if (window.navigator.canShare && window.navigator.canShare(shareData)) {
          await window.navigator.share(shareData);
        }
      } catch (e) {
      }
    };
    const canShowPaymentForm = isCustomer &&
      (txIsPreAccepted(currentTransaction) || txIsPaymentPending(currentTransaction) && txLastTransition(currentTransaction) === TRANSITION_REQUEST_MANUAL_PAYMENT);


    return (
      <div className={classes}>
        <div className={css.container}>
          <div className={css.txInfo}>
            <DetailCardImage
              rootClassName={css.imageWrapperMobile}
              avatarWrapperClassName={css.avatarWrapperMobile}
              listingTitle={listingTitle}
              image={firstImage}
              provider={currentProvider}
              isCustomer={isCustomer}
            />
            {isProvider ? (
              <div className={css.avatarWrapperProviderDesktop}>
                <AvatarLarge user={currentCustomer} className={css.avatarDesktop} />
                {isSwimPlusCustomer && (
                  <ConfirmedBadge />
                )}
              </div>
            ) : null}

            <PanelHeading
              panelHeadingState={stateData.headingState}
              transactionRole={transactionRole}
              providerName={authorDisplayName}
              customerName={customerDisplayName}
              isCustomerBanned={isCustomerBanned}
              listingId={currentListing.id && currentListing.id.uuid}
              listingTitle={listingTitle}
              listingDeleted={listingDeleted}
              isSwimPlusCustomer={isSwimPlusCustomer}
            />
            {canShowPaymentForm && (
              <div className={css.paymentFormWithPaymentIntent}>
                <div className={css.paymentMethodDisclaimer}>
                  <FormattedMessage
                    id="PaymentMethodsForm.paymentMethodDisclaimer"
                    values={{ paymentMethodsPageLink }}
                  />
                </div>
                <PaymentFormWithPaymentIntent
                  onSubmit={handleConfirmPayment}
                  isLoading={isConfirmManualPaymentMultiBookingLoading}
                  handleCardSetupError={confirmManualPaymentMultiBookingError}
                />
              </div>
            )}
            <div className={css.bookingDetailsMobile}>
              <AddressLinkMaybe
                rootClassName={css.addressMobile}
                location={location}
                geolocation={geolocation}
                showAddress={stateData.showAddress}
              />
              <div className={css.bookingInfoWrapper}>
                <span className={css.bookingHeading}>{bookingHeadingText}</span>
                <BookingTimeBreakdown className={css.bookingTimeBreakdown}
                                      transaction={currentTransaction} withTitle />
                <BookingParticipantBreakdown className={css.bookingParticipantBreakdown}
                                             transaction={currentTransaction} />
              </div>
              <PriceBreakdownMaybe transaction={currentTransaction}
                                   transactionRole={transactionRole} />
            </div>

            {savePaymentMethodFailed ? (
              <p className={css.genericError}>
                <FormattedMessage
                  id="TransactionPanel.savePaymentMethodFailed"
                  values={{ paymentMethodsPageLink }}
                />
              </p>
            ) : null}
            {stateData.showAddress && isMobile && (
              <div className={css.desktopShareButton}>
                <SecondaryButton
                  onClick={() => handleShareBooking()}
                >
                  <FormattedMessage id="TransactionPanel.shareButton" />
                </SecondaryButton>
              </div>
            )}
            <FeedSection
              rootClassName={css.feedContainer}
              currentTransaction={currentTransaction}
              currentUser={currentUser}
              fetchMessagesError={fetchMessagesError}
              fetchMessagesInProgress={fetchMessagesInProgress}
              initialMessageFailed={initialMessageFailed}
              messages={[last(messages)]}
              oldestMessagePageFetched={oldestMessagePageFetched}
              onOpenReviewModal={this.onOpenReviewModal}
              onShowMoreMessages={() => onShowMoreMessages(currentTransaction.id)}
              totalMessagePages={totalMessagePages}
            />
            {showSendMessageForm ? (
              <NamedLink className={css.sendMessageLink} name="InboxPage"
                         params={{ id: currentTransaction.id.uuid }}>
                <FormattedMessage id="TransactionPage.OpenConversation" />
              </NamedLink>
            ) : (
              <div className={css.sendingMessageNotAllowed}>{sendingMessageNotAllowed}</div>
            )}

            {showButtons ? (
              <div className={css.mobileActionButtons}>{actionButtons}</div>
            ) : null}
            {showSimilarPools ? (
              <SectionSimilarListings
                suggestionSearchParams={suggestionSearchParams}
                listings={similarListings}
                searchInProgress={fetchSimilarListingsInProgress}
                searchListingsError={fetchSimilarListingsError}
              />
            ) : null}
          </div>

          <div className={css.asideDesktop}>
            <div className={css.detailCard}>
              <DetailCardImage
                avatarWrapperClassName={css.avatarWrapperDesktop}
                listingTitle={listingTitle}
                image={firstImage}
                provider={currentProvider}
                isCustomer={isCustomer}
              />
              <DetailCardHeadingsMaybe
                showDetailCardHeadings={stateData.showDetailCardHeadings}
                listingTitle={listingTitle}
                subTitle={bookingSubTitle}
                location={location}
                geolocation={geolocation}
                showAddress={stateData.showAddress}
                listing={currentListing}
              />
              {stateData.showBookingPanel ? (
                <BookingPanelWithContext
                  className={css.bookingPanel}
                  listing={currentListing}
                  transaction={currentTransaction}
                  withoutBorder
                />
              ) : (
                <div className={css.bookingInfoWrapperDesktop}>
                  <span className={css.bookingHeading}>{bookingHeadingText}</span>
                  <BookingTimeBreakdown className={css.bookingTimeBreakdown}
                                        transaction={currentTransaction} withTitle />
                  <BookingParticipantBreakdown className={css.bookingParticipantBreakdown}
                                               transaction={currentTransaction} />
                </div>
              )}
              <PriceBreakdownMaybe
                className={css.breakdownContainer}
                transaction={currentTransaction}
                transactionRole={transactionRole}
              />

              {showButtons ? (
                <div className={css.desktopActionButtons}>{actionButtons}</div>
              ) : null}
              {stateData.showNoCancellation && (
                <TextContainer className={css.noCancellation} text={noCancellationText} />
              )}
            </div>
          </div>
        </div>
        <ReviewModal
          id="ReviewOrderModal"
          isOpen={this.state.isReviewModalOpen}
          onCloseModal={() => this.setState({ isReviewModalOpen: false })}
          onManageDisableScrolling={onManageDisableScrolling}
          onSubmit={this.onSubmitReview}
          revieweeName={otherUserDisplayName}
          reviewSent={this.state.reviewSubmitted}
          sendReviewInProgress={sendReviewInProgress}
          sendReviewError={sendReviewError}
        />
        <DeclineBookingModal
          id="DeclineBookingModal"
          isOpen={this.state.isDeclineBookingModalOpen}
          isHighDeclineRate={isHighDeclineRate}
          onCloseModal={() => this.setState({ isDeclineBookingModalOpen: false })}
          onManageDisableScrolling={onManageDisableScrolling}
          onOkModal={this.handleOnDeclineBooking}
        />
        <CancelBookingModal
          id="CancelBookingModal"
          isProvider={isProvider}
          idDescriptionText={
            isProvider
              ? 'CancelBookingModal.descriptionForProvider'
              : (!txIsAcceptedRefundable(transaction) ? 'CancelBookingModal.descriptionForCustomer' : 'CancelBookingModal.descriptionForCustomerFull')
          }
          isOpen={this.state.isCancelBookingModalOpen}
          onCloseModal={() => this.setState({ isCancelBookingModalOpen: false })}
          onManageDisableScrolling={onManageDisableScrolling}
          onOkModal={this.handleOnCancelBooking}
        />
        <CancelBookingModal
          id="RefundBookingAfterDeliveredModal"
          idDescriptionText={
            isProvider
              ? 'CancelBookingModal.descriptionForProvider'
              : 'CancelBookingModal.descriptionForCustomer'
          }
          isOpen={this.state.isRefundBookingAfterDeliveredModalOpen}
          onCloseModal={() => this.setState({ isRefundBookingAfterDeliveredModalOpen: false })}
          onManageDisableScrolling={onManageDisableScrolling}
          onOkModal={this.handleOnRefundBookingAfterDelivered}
        />
      </div>
    );
  }
}

TransactionPanelComponent.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  acceptSaleError: null,
  declineSaleError: null,
  fetchMessagesError: null,
  initialMessageFailed: false,
  savePaymentMethodFailed: false,
  sendMessageError: null,
  sendReviewError: null,
  nextTransitions: null,
  cancelBookingError: null,
};

TransactionPanelComponent.propTypes = {
  rootClassName: string,
  className: string,

  currentUser: propTypes.currentUser,
  transaction: propTypes.transaction.isRequired,
  totalMessagePages: number.isRequired,
  oldestMessagePageFetched: number.isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  initialMessageFailed: bool,
  savePaymentMethodFailed: bool,
  fetchMessagesInProgress: bool.isRequired,
  fetchMessagesError: propTypes.error,
  sendReviewInProgress: bool.isRequired,
  sendReviewError: propTypes.error,
  onManageDisableScrolling: func.isRequired,
  onShowMoreMessages: func.isRequired,
  onSendMessage: func.isRequired,
  onSendReview: func.isRequired,
  onSubmitBookingRequest: func.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  nextTransitions: array,

  // Sale related props
  onAcceptSale: func.isRequired,
  onDeclineSale: func.isRequired,
  acceptInProgress: bool.isRequired,
  declineInProgress: bool.isRequired,
  acceptSaleError: propTypes.error,
  declineSaleError: propTypes.error,
  cancelInProgress: bool.isRequired,
  cancelBookingError: propTypes.error,
  onCancelBooking: func.isRequired,

  // from injectIntl
  intl: intlShape,
};

const TransactionPanel = injectIntl(TransactionPanelComponent);

export default TransactionPanel;
